import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const link = (text: string, to: string) => (
  <a
    href={to}
    className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
  >
    {text}
  </a>
);

const AnswerBlock = ({ children }: { children: ReactNode }) => (
  <div className="not-prose mb-5 rounded-xl border border-accent/30 bg-accent/5 p-4 sm:p-5">
    <p className="text-[1.0625rem] leading-relaxed text-foreground">
      {children}
    </p>
  </div>
);

const a_beam =
  "The BEAM F3 method is a quick way to find a standard scalp spot \u2014 the F3 EEG position \u2014 from three head measurements, used as a stand-in for the left dorsolateral prefrontal cortex. It was developed to make standard TMS easier to set up without neuronavigation equipment.";

const a_fmri =
  "A resting-state brain MRI shows which part of your prefrontal cortex works in sync or in opposition to a deeper mood-related area (the subgenual cingulate). SAINT\u00ae software uses that connectivity data to pick the exact spot to stimulate for you \u2014 a target that is individual, not an average.";

const a_same =
  "No. Head measurements estimate where the region usually sits on an average head; they can\u2019t see brain function. The connectivity target is different for each person, so a scalp estimate and a scan-based target don\u2019t reliably match. That is why SAINT\u00ae requires the scan.";

const a_saint =
  "No. SAINT\u00ae requires a personalized MRI target, FDA-cleared targeting software, and the Magnus Medical system \u2014 delivered only by providers on Magnus Medical\u2019s locator. A clinic that says head measurements use \u201cthe same measurements and algorithm\u201d as an fMRI is not describing SAINT\u00ae.";

const a_better =
  "The SAINT\u00ae controlled trial (79% remission) used MRI-guided targeting; those results don\u2019t transfer to programs that target differently. Ask your doctor which method a clinic uses and what results it has. Individual results vary.";

const faqs: LearnFAQ[] = [
  { question: "What is the BEAM F3 method?", answer: a_beam },
  {
    question: "What is fMRI-guided (connectivity-guided) TMS?",
    answer: a_fmri,
  },
  {
    question: "Can head measurements find the same target as an fMRI?",
    answer: a_same,
  },
  { question: "Is BEAM F3 the same as SAINT?", answer: a_saint },
  {
    question: "Which is better for treatment-resistant depression?",
    answer: a_better,
  },
];

export const pageBeamF3VsFmri: LearnPageData = {
  slug: "beam-f3-vs-fmri-tms",
  title: "BEAM F3 vs fMRI Targeting for TMS: What\u2019s the Difference?",
  description:
    "BEAM F3 estimates a brain target from head measurements; fMRI-guided SAINT\u00ae finds your own target from a brain scan. Here\u2019s why that matters.",
  canonical: "/learn/beam-f3-vs-fmri-tms",
  h1: "BEAM F3 vs fMRI-Guided TMS: How Clinics Aim the Magnet",
  breadcrumbLabel: "BEAM F3 vs fMRI Targeting",
  crumbs: baseCrumbs("BEAM F3 vs fMRI Targeting"),
  shortAnswer:
    "BEAM F3 estimates where a brain region usually sits from three head measurements. fMRI-guided SAINT\u00ae finds your own target from a brain scan. The difference matters because the connectivity target is different for each person, and only the scan-based, FDA-cleared approach is SAINT\u00ae.",
  keyTakeaways: [
    "BEAM F3 finds a standard scalp spot (the F3 position) from head measurements \u2014 a population stand-in, not a personal target.",
    "fMRI-guided SAINT\u00ae uses your own brain\u2019s functional connectivity to pick where to stimulate.",
    "Head measurements can\u2019t see brain function, so they can\u2019t reliably match a scan-based target.",
    "SAINT\u00ae requires the MRI target, FDA-cleared software, and the Magnus Medical system \u2014 only from verified providers.",
    "The 79% remission result comes from MRI-guided SAINT\u00ae; it doesn\u2019t transfer to a different targeting method.",
  ],
  sections: [
    {
      heading: "What is the BEAM F3 method?",
      body: (
        <>
          <AnswerBlock>{a_beam}</AnswerBlock>
          {P(
            "The \u201cF3\u201d in BEAM F3 refers to the F3 electrode position \u2014 a standard landmark on the scalp that approximates the left dorsolateral prefrontal cortex, the area most TMS protocols target for depression. Measuring the head and landing on that average coordinate is fast and needs no scanner, which is why many standard-TMS clinics use it.",
          )}
          {P(
            "But \u201caverage coordinate\u201d is the key phrase: BEAM F3 tells a clinic where that region usually sits, not where it sits in you, and nothing about how it communicates with the deeper mood circuits TMS is meant to influence.",
          )}
        </>
      ),
    },
    {
      heading: "What is fMRI-guided (connectivity-guided) TMS?",
      body: (
        <>
          <AnswerBlock>{a_fmri}</AnswerBlock>
          {P(
            "Before SAINT\u00ae treatment, you have a resting-state functional MRI. Software analyzes the functional connectivity \u2014 how brain regions talk to each other \u2014 and selects the exact spot on your prefrontal cortex that has the strongest link to the subgenual cingulate, a deeper area involved in mood. That spot is unique to you and becomes the target for all 50 sessions.",
          )}
        </>
      ),
    },
    {
      heading: "Two ways to aim the magnet",
      body: (
        <>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <svg
                viewBox="0 0 200 120"
                className="mb-3 h-28 w-full"
                role="img"
                aria-label="Head outline with tape-measure lines pointing to one average spot"
              >
                <ellipse
                  cx="100"
                  cy="62"
                  rx="42"
                  ry="52"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-primary"
                />
                <line
                  x1="40"
                  y1="58"
                  x2="160"
                  y2="58"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  className="text-accent"
                />
                <line
                  x1="100"
                  y1="10"
                  x2="100"
                  y2="114"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  className="text-accent"
                />
                <circle
                  cx="78"
                  cy="50"
                  r="6"
                  fill="currentColor"
                  className="text-accent"
                />
              </svg>
              <p className="text-sm font-semibold text-foreground">
                Head measurements
              </p>
              <p className="text-sm text-foreground/80">
                \u2192 one average spot on an average head.
              </p>
            </div>
            <div className="rounded-xl border border-accent/40 bg-accent/10 p-4">
              <svg
                viewBox="0 0 200 120"
                className="mb-3 h-28 w-full"
                role="img"
                aria-label="Brain scan outline with a highlighted target unique to that brain"
              >
                <path
                  d="M55 95 C30 70 35 35 70 28 C80 14 120 14 130 28 C165 35 170 70 145 95 C135 108 65 108 55 95 Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-primary"
                />
                <path
                  d="M100 28 L100 60"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  className="text-foreground/40"
                />
                <path
                  d="M70 60 Q100 78 130 60"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  className="text-foreground/40"
                />
                <circle
                  cx="82"
                  cy="52"
                  r="7"
                  fill="currentColor"
                  className="text-accent"
                />
                <circle
                  cx="118"
                  cy="74"
                  r="5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-accent"
                />
              </svg>
              <p className="text-sm font-semibold text-foreground">
                Brain MRI (fMRI)
              </p>
              <p className="text-sm text-foreground/80">
                \u2192 your spot, from your own connectivity.
              </p>
            </div>
          </div>
          {P(
            <>
              Both approaches end with a coil on your head. The difference is
              how that coordinate is chosen.{" "}
              {link(
                "Does SAINT\u00ae require an MRI?",
                "/learn/does-saint-tms-require-an-mri",
              )}{" "}
              explains why the scan is required, not optional.
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Can head measurements find the same target as an fMRI?",
      body: (
        <>
          <AnswerBlock>{a_same}</AnswerBlock>
          {P(
            "A scalp position is a reasonable starting point, but the part of the prefrontal cortex that best connects to your subgenual cingulate varies from person to person. Measuring the outside of your head can\u2019t reveal that internal connectivity, so a head-measurement target and a scan-based target can land in different places.",
          )}
        </>
      ),
    },
    {
      heading: "Is BEAM F3 the same as SAINT?",
      body: (
        <>
          <AnswerBlock>{a_saint}</AnswerBlock>
          {P(
            <>
              SAINT\u00ae (Stanford Accelerated Intelligent Neuromodulation
              Therapy) is one specific, FDA-cleared protocol: the Magnus
              Neuromodulation System, its own targeting software, and delivery
              only by providers listed on{" "}
              {link(
                "Magnus Medical\u2019s provider locator",
                "/learn/what-is-a-verified-saint-provider",
              )}
              . Other clinics may use an MRI to aim TMS without offering
              SAINT\u00ae \u2014 that is a different treatment. See our{" "}
              {link(
                "SAINT\u00ae vs lower-cost \u201caccelerated TMS\u201d comparison",
                "/learn/saint-vs-accelerated-tms",
              )}
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Which is better for treatment-resistant depression?",
      body: (
        <>
          <AnswerBlock>{a_better}</AnswerBlock>
          {P(
            "We make no head-to-head superiority claim. What we can say factually: in the 2022 double-blind, sham-controlled randomized trial, 79% of the active-treatment group reached remission (Cole et al., Am J Psychiatry 2022;179(2):132\u2013141), and that trial used MRI-guided targeting. Results from those trials can\u2019t be assumed for a program that targets differently. Ask your doctor which method a clinic uses and what results it has published.",
          )}
        </>
      ),
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification \u2014 Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/beam-f3-vs-fmri-tms#webpage`,
        name: "BEAM F3 vs fMRI-Guided TMS: How Clinics Aim the Magnet",
        url: `${SITE_URL}/learn/beam-f3-vs-fmri-tms`,
        description:
          "BEAM F3 estimates a brain target from head measurements; fMRI-guided SAINT\u00ae finds your own target from a brain scan. Here\u2019s why that matters.",
        about: [
          {
            "@type": "MedicalTherapy",
            name: "Transcranial Magnetic Stimulation (TMS)",
          },
          {
            "@type": "MedicalTherapy",
            name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT\u00ae)",
          },
        ],
        reviewedBy: {
          name: "Daniel Pietras, MD",
          url: `${SITE_URL}/dr-daniel-pietras-md`,
        },
        lastReviewed: "2026-11-03",
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/learn/beam-f3-vs-fmri-tms#faq`),
      breadcrumbJsonld(baseCrumbs("BEAM F3 vs fMRI Targeting")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "Does SAINT\u00ae Require an MRI?",
      href: "/learn/does-saint-tms-require-an-mri",
      description: "Why the brain scan is part of the SAINT\u00ae protocol",
    },
    {
      title: "fMRI-Guided TMS for Depression",
      href: "/learn/fmri-guided-tms-for-depression",
      description: "How personalized targeting works in SAINT\u00ae",
    },
    {
      title: "What Is a Verified SAINT\u00ae Provider?",
      href: "/learn/what-is-a-verified-saint-provider",
      description: "How to confirm a clinic on Magnus Medical\u2019s locator",
    },
    {
      title: "SAINT\u00ae vs Lower-Cost Accelerated TMS",
      href: "/learn/saint-vs-accelerated-tms",
      description: "What a copied 5-day schedule is and isn\u2019t",
    },
  ],
  safetyBlock:
    "SAINT\u00ae is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT\u00ae is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. Neuro Wellness TMS Centers of America does not name or endorse any competitor. Individual results vary. No outcome is guaranteed. Coverage varies by plan.",
  ctaBand: {
    headline: "See if SAINT\u00ae is a fit, free",
    primaryText: "See if I may be a candidate",
    primaryHref: "/am-i-a-candidate",
    secondaryText: "Talk to a real person",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
};
